import {DEFAULT_ANIMATION_DURATION} from '@contentlab/components/video-display/captions-rendering/animation/defaults';
import {
  Word,
  Animation,
  StyleConfig,
  Background,
} from '@contentlab/types/captions';

// Box animation specific interface
export interface HighlightBoxWordDrawingParams {
  word: Word;
  x: number;
  y: number;
  context: CanvasRenderingContext2D;
  fontColor: string;
  fontSize: number;
  strokeColor: string;
  strokeWidth: number;
  style: StyleConfig;
  currentTime: number;
  animation: Animation;
  lineHeight: number;
}

// Animation scale calculation
export function getAnimationScale(
  animation_duration: number,
  currentTime: number,
  max_scale: number,
  word: Word
): number {
  const actual_duration = Math.min(
    word.end - word.start,
    animation_duration * 1000
  );
  const elapsed = currentTime - word.start;
  const progress = elapsed / actual_duration;

  // Animation phases: 0-0.5 scale up, 0.5-1 scale down
  const peakTime = 0.5;
  if (progress <= peakTime) {
    const scaleUpProgress = progress / peakTime;
    return 1 + (max_scale - 1) * scaleUpProgress;
  }
  if (progress < 1) {
    const scaleDownProgress = (progress - peakTime) / (1 - peakTime);
    return max_scale - (max_scale - 1) * scaleDownProgress;
  }
  return 1;
}

// Animation opacity calculation
export function getAnimaitonBoxOpacity(
  animation_duration: number,
  currentTime: number,
  word: Word
): number {
  const actual_duration = Math.min(
    word.end - word.start,
    animation_duration * 1000
  );
  const elapsed = currentTime - word.start;
  const progress = elapsed / actual_duration;
  return Math.min(progress / 0.5, 1);
}

// Word rendering helper
export function drawWordHighlightBox(
  params: HighlightBoxWordDrawingParams,
  isCurrentWord: (word: Word, currentTime: number) => boolean,
  getHighlightFillColor: (
    fontColor: string,
    currentTime: number,
    word: Word,
    animation: Animation
  ) => string,
  drawBackground: (
    context: CanvasRenderingContext2D,
    background: Background,
    lines: string[],
    fontSize: number,
    position: {x: number; lineHeight: number; startY: number}
  ) => void,
  drawDropShadow: (
    context: CanvasRenderingContext2D,
    style: StyleConfig,
    fontColor: string,
    x: number,
    y: number,
    text: string,
    strokeWidth?: number,
    scaleFactor?: number
  ) => void,
  drawStroke: (
    context: CanvasRenderingContext2D,
    strokeColor: string,
    strokeWidth: number,
    text: string,
    x: number,
    y: number
  ) => void
): void {
  const {
    word,
    x,
    y,
    context,
    fontColor,
    fontSize,
    strokeColor,
    strokeWidth,
    style,
    currentTime,
    animation,
    lineHeight: textLineHeight,
  } = params;

  const isCurrent = isCurrentWord(word, currentTime);
  const fillColor = getHighlightFillColor(
    fontColor,
    currentTime,
    word,
    animation
  );
  const maxScale = style.animation?.wordMaxScaling ?? 1;
  const animationDuration = animation.duration ?? DEFAULT_ANIMATION_DURATION;

  const scale = isCurrent
    ? getAnimationScale(animationDuration, currentTime, maxScale, word)
    : 1;

  context.save();

  if (isCurrent) {
    // Apply scaling transformation for current word
    context.translate(x, y);
    context.scale(scale, scale);
    context.translate(-x, -y);

    if (style.animation?.background) {
      const opacity = getAnimaitonBoxOpacity(
        animationDuration,
        currentTime,
        word
      );
      const background = {
        ...style.animation.background,
        color: {...style.animation.background.color, opacity},
      };
      context.globalCompositeOperation = 'destination-over';
      drawBackground(context, background, [word.text], fontSize, {
        x,
        lineHeight: textLineHeight / maxScale,
        startY: y,
      });
      context.globalCompositeOperation = 'source-over';
    }
  }

  // Draw drop shadow
  if (style.styling.dropShadow) {
    drawDropShadow(context, style, fontColor, x, y, word.text, strokeWidth);
  }

  // Draw stroke
  if (strokeWidth > 0 && strokeColor !== 'transparent') {
    drawStroke(context, strokeColor, strokeWidth, word.text, x, y);
  }

  // Draw fill
  context.fillStyle = fillColor;
  context.fillText(word.text, x, y);

  context.restore();
}
